import {
  CHAT_TRACE_EVENT_PART_TYPE,
  CHAT_TRACE_TIMELINE_PART_TYPE,
  type ConversationMessageMetadata,
  type ReadPagesToolOutput,
} from "@shaarp/common";
import type { ToolUIPart, UIMessage } from "ai";
import { textBlockSeparator } from "@shaarp/common";
import { sourceLink } from "@/lib/source-link";

export type AssistantPart =
  UIMessage<ConversationMessageMetadata>["parts"][number];

/**
 * A line the model wrote to announce its next step, moved out of the answer
 * into the card of the work. Not a part the stream sends: the card draws it
 * under the reflection it belongs to, in the reader's language — it is answer
 * text, not the provider's reasoning summary.
 */
export const NARRATION_PART_TYPE = "narration";

export type NarrationPart = {
  readonly type: typeof NARRATION_PART_TYPE;
  readonly text: string;
};

/**
 * Where a clarifying question was answered, between the work of the turn that
 * asked it and the work of the turn that took the answer in: the card shows
 * both turns as one timeline, with the answer as a step between them.
 */
export const CLARIFICATION_ANSWERED_PART_TYPE = "clarificationAnswered";

export type ClarificationAnsweredPart = {
  readonly type: typeof CLARIFICATION_ANSWERED_PART_TYPE;
};

/**
 * A part of the card of the work: one the stream sent, a narration, or the
 * point where a clarification was answered.
 */
export type ResearchPart = {
  part: AssistantPart | NarrationPart | ClarificationAnsweredPart;
  index: number;
};

export function isNarrationPart(
  part: ResearchPart["part"]
): part is NarrationPart {
  return part.type === NARRATION_PART_TYPE;
}

export function isClarificationAnsweredPart(
  part: ResearchPart["part"]
): part is ClarificationAnsweredPart {
  return part.type === CLARIFICATION_ANSWERED_PART_TYPE;
}

/** A part the stream sent, as opposed to one the card lays out itself. */
export function isStreamedPart(
  part: ResearchPart["part"]
): part is AssistantPart {
  return !isNarrationPart(part) && !isClarificationAnsweredPart(part);
}

/** The line, trimmed, as the card keeps it. */
function narrationPart(text: string, index: number): ResearchPart {
  return { part: { type: NARRATION_PART_TYPE, text: text.trim() }, index };
}

type IndexedPart = { part: AssistantPart; index: number };

export type AssistantRenderItem =
  | { kind: "part"; part: AssistantPart; index: number }
  | { kind: "researchGroup"; startIndex: number; parts: ResearchPart[] }
  | { kind: "companyList"; startIndex: number; parts: ToolUIPart[] };

export const SEARCH_TOOL_TYPES = new Set([
  "tool-webSearch",
  "tool-searchCompanyRegistry",
  "tool-scrapeWebsite",
  "tool-readPages",
]);
// Reasoning interleaves with searches during the research phase; grouping them
// together avoids a verbose stack of alternating "thought" / "search" pills.
//
// `getCompanyData` joins the group as a row only: its card is the one thing
// the user must see without unfolding anything, so it is drawn under the
// card, never inside it — buried in a collapsed block it went unseen on the
// very path where no web search follows.

/**
 * The turn's timeline, stored with every answer, or one of its events as the
 * stream sends them: the work card times its rows from it. It is no content
 * of the thread, and must not cut a card in two.
 */
export function isTimelinePart(part: { readonly type: string }): boolean {
  return (
    part.type === CHAT_TRACE_TIMELINE_PART_TYPE ||
    part.type === CHAT_TRACE_EVENT_PART_TYPE
  );
}

export type ThinkingStepKind =
  | "clarification"
  | "reflection"
  | "webSearch"
  | "readPages"
  | "registry"
  | "companyData"
  | "hubspot"
  | "seliaris"
  | "tactics"
  | "people"
  | "contact";

/**
 * The tools whose call is a step of the work behind the answer, and the row
 * each one feeds. They join the card as a row of its timeline; the ones with
 * a card of their own (CRM results, company data) show it under the card. A
 * scrape is a page read like any other, the three tactics tools are one
 * consultation. The company list, the documents and the proposed tactic are
 * the answer, and a clarification question is a question back: none of them
 * is a step.
 */
export const TRACE_STEP_KIND_BY_TOOL_TYPE = {
  "tool-webSearch": "webSearch",
  "tool-readPages": "readPages",
  "tool-scrapeWebsite": "readPages",
  "tool-searchCompanyRegistry": "registry",
  "tool-getCompanyData": "companyData",
  "tool-searchHubspotCompanies": "hubspot",
  "tool-searchSeliarisCompanies": "seliaris",
  "tool-listUserTactics": "tactics",
  "tool-searchUserTactics": "tactics",
  "tool-activateUserTactic": "tactics",
  "tool-searchPeople": "people",
  "tool-previewContactReveal": "contact",
} as const satisfies Readonly<Record<string, ThinkingStepKind>>;

/** A tool whose call is a step of the card. */
export type TraceToolType = keyof typeof TRACE_STEP_KIND_BY_TOOL_TYPE;

export const TRACE_TOOL_TYPES: ReadonlySet<string> = new Set(
  Object.keys(TRACE_STEP_KIND_BY_TOOL_TYPE)
);

/** Whether a part's tool is a step of the card. */
export function isTraceToolType(type: string): type is TraceToolType {
  return TRACE_TOOL_TYPES.has(type);
}
const TRACE_PART_TYPES = new Set([...TRACE_TOOL_TYPES, "reasoning"]);

/**
 * A registry call that searched nothing: it was declined because the web
 * search it stands in for answered, or its criteria could not be searched at
 * all. It answers the model in words, and has no line to show the reader.
 */
function isEmptyRegistryCall(part: AssistantPart): boolean {
  const toolPart = part as ToolUIPart;
  return (
    part.type === "tool-searchCompanyRegistry" &&
    toolPart.state === "output-available" &&
    typeof toolPart.output === "string"
  );
}

/**
 * The pages read: one per scrape, and every page a `readPages` call returned.
 * The persisted output keeps the pages with their content emptied, so the
 * count survives a reload.
 */
export function countPagesRead(searchParts: ToolUIPart[]): number {
  return (
    searchParts.filter((part) => part.type === "tool-scrapeWebsite").length +
    searchParts
      .filter((part) => part.type === "tool-readPages")
      .reduce(
        (total, part) =>
          total +
          ((part.output as Partial<ReadPagesToolOutput> | undefined)?.pages
            ?.length ?? 0),
        0
      )
  );
}

type ResearchGroupItem = Extract<
  AssistantRenderItem,
  { kind: "researchGroup" }
>;

/**
 * The last item, when it is a text run a continuation can be glued onto.
 */
function lastMergeableText(
  items: AssistantRenderItem[]
): Extract<AssistantRenderItem, { kind: "part" }> | null {
  const last = items[items.length - 1];
  return last?.kind === "part" &&
    last.part.type === "text" &&
    last.part.text.trim().length > 0
    ? last
    : null;
}

/**
 * The group a research part joins, always above the answer being written.
 *
 * A round searches again after it has started writing — a scrape to check a
 * figure, a reasoning summary between two paragraphs — and those parts arrive
 * after the text they interrupt. Left where they arrive they end up under the
 * finished answer, because the continuation is stitched back onto the text run
 * above them: the reader gets a complete reply with the cards that produced it
 * hanging off the bottom. So a group that opens right after a text run is
 * inserted before it, joining the research block already there rather than
 * opening a second one.
 */
function openResearchGroup(
  items: AssistantRenderItem[],
  index: number
): ResearchGroupItem {
  if (!lastMergeableText(items)) {
    const group: ResearchGroupItem = {
      kind: "researchGroup",
      startIndex: index,
      parts: [],
    };
    items.push(group);
    return group;
  }

  // The company list sits between the research block and the answer; a late
  // search still joins that block rather than opening a second one.
  const textIndex = items.length - 1;
  const previous = items
    .slice(0, textIndex)
    .reverse()
    .find((item) => item.kind !== "companyList");
  if (previous?.kind === "researchGroup") {
    return previous;
  }

  const group: ResearchGroupItem = {
    kind: "researchGroup",
    startIndex: index,
    parts: [],
  };
  items.splice(textIndex, 0, group);
  return group;
}

// The longest line still read as the model saying what it does next.
const NARRATION_MAX_CHARS = 200;



/**
 * A text run that only announces the next research step ("Je lis les
 * annuaires les plus riches."): one short line. An answer interrupted by a
 * search to check a figure is longer, or spans paragraphs, a list, a table.
 * A line carrying a citation or a company marker is the answer too: only the
 * answer's renderer turns a marker into a pill or a chip.
 */
function isNarration(text: string): boolean {
  const line = text.trim();
  return (
    line.length > 0 &&
    line.length <= NARRATION_MAX_CHARS &&
    !line.includes("\n") &&
    !line.includes("[@shaarp/")
  );
}

/**
 * Whether a live answer's text is the answer itself, not yet a line that may
 * still turn out to announce the next call. The same rule the API applies
 * before it stops streaming the model's reasoning.
 */
export function isAnswerUnderway(text: string): boolean {
  return text.trim().length > 0 && !isNarration(text);
}

/**
 * The group a research part joins when the text right before it only announced
 * it: the sentence leaves the answer — where it would be glued to the first
 * words of the reply — and becomes a thought of the research block, which also
 * names it in the folded header while the step runs. Null when the text before
 * is the answer itself.
 */
function openNarratedResearchGroup(
  items: AssistantRenderItem[],
  { isAnswerToCome }: { isAnswerToCome: boolean }
): ResearchGroupItem | null {
  const narration = lastMergeableText(items);
  if (
    !narration ||
    narration.part.type !== "text" ||
    !isAnswerToCome ||
    !isNarration(narration.part.text)
  ) {
    return null;
  }

  items.pop();
  // The company list sits between the research block and the answer; the
  // sentence joins that block rather than opening a second one under the list.
  const previous = [...items]
    .reverse()
    .find((item) => item.kind !== "companyList");
  const group =
    previous?.kind === "researchGroup"
      ? previous
      : openResearchGroup(items, narration.index);
  group.parts.push(narrationPart(narration.part.text, narration.index));
  return group;
}

/**
 * Concatenates consecutive text parts into one.
 *
 * Resuming an interrupted answer streams the continuation as a new text part,
 * and rendering each part as its own markdown block would split the sentence in
 * two — the continuation is written to be glued onto the end of the previous
 * text, not to open a paragraph. Each merged run keeps the index of its first
 * part so React keys stay stable while the run grows during streaming.
 */
function mergeAdjacentTextParts(parts: AssistantPart[]): IndexedPart[] {
  const merged: IndexedPart[] = [];

  parts.forEach((part, index) => {
    const previous = merged[merged.length - 1];
    if (part.type === "text" && previous?.part.type === "text") {
      merged[merged.length - 1] = {
        part: { ...part, text: previous.part.text + part.text },
        index: previous.index,
      };
      return;
    }
    merged.push({ part, index });
  });

  return merged;
}

/**
 * Collapses a run of reasoning + web-search / scrape parts into a single group
 * so the whole research phase renders as one compact, foldable block above the
 * answer, instead of a stack of alternating cards.
 */
export function buildAssistantRenderItems(
  parts: AssistantPart[],
  { isLive = false }: { isLive?: boolean } = {}
): AssistantRenderItem[] {
  const items: AssistantRenderItem[] = [];
  let group: Extract<AssistantRenderItem, { kind: "researchGroup" }> | null =
    null;
  const merged = mergeAdjacentTextParts(parts);
  // Whether a tool call or a thought came since the last text: the next text
  // is then another step's, not the continuation of a cut sentence.
  let hasPartSinceText = false;
  const lastTextPosition = merged.reduce(
    (last, { part }, position) =>
      part.type === "text" && part.text.trim().length > 0 ? position : last,
    -1
  );

  // A step marker seen while no card is open, handed to the next one: it is
  // where the reflection before that card's first call began.
  let pendingStepStart: ResearchPart | null = null;

  merged.forEach(({ part, index }, position) => {
    // The stream emits empty placeholder text parts and one `step-start` per
    // model step between tool calls; the backend persists neither. A marker
    // never opens or splits a card, so a run of searches stays contiguous
    // during streaming — but it goes into the card it falls in, where it
    // marks the reflection that opens each model step.
    if (part.type === "step-start") {
      if (group) {
        group.parts.push({ part, index });
      } else {
        pendingStepStart = { part, index };
      }
      return;
    }
    if (isEmptyRegistryCall(part) || isTimelinePart(part)) {
      return;
    }
    if (part.type === "text" && part.text.trim().length === 0) {
      return;
    }
    const isSeparatedText = part.type === "text" && hasPartSinceText;
    hasPartSinceText = part.type !== "text";
    // A long answer is resumed server-side and arrives as several text parts,
    // the join often landing mid-sentence. Rendered separately they would be
    // two independent markdown documents — a split list or table would come out
    // broken — so consecutive text parts are stitched back into one.
    if (part.type === "text") {
      const target = lastMergeableText(items);
      if (target) {
        const targetPart = target.part as Extract<
          AssistantPart,
          { type: "text" }
        >;
        const separator = isSeparatedText
          ? textBlockSeparator(targetPart.text, part.text)
          : "";
        items[items.length - 1] = {
          ...target,
          part: { ...targetPart, text: targetPart.text + separator + part.text },
        };
        group = null;
        return;
      }
    }
    // Searches and deposits interleave (search, add, search, add…): the
    // deposits gather into one card placed where the first one landed, and do
    // not close the research block, so the thread reads research, list, answer.
    if (part.type === "tool-addCompanies") {
      const list = items.find(
        (item): item is Extract<AssistantRenderItem, { kind: "companyList" }> =>
          item.kind === "companyList"
      );
      if (list) {
        list.parts.push(part as ToolUIPart);
      } else {
        items.push({ kind: "companyList", startIndex: index, parts: [part as ToolUIPart] });
      }
      return;
    }
    if (TRACE_PART_TYPES.has(part.type)) {
      // Only a call announces a step: a reasoning summary after a line leaves
      // that line in the answer, which it introduces.
      if (!group && part.type !== "reasoning") {
        group = openNarratedResearchGroup(items, {
          // The sentence only reads as a step once an answer follows it, or
          // may still: on a finished message it can be all the text there is.
          isAnswerToCome: isLive || position < lastTextPosition,
        });
      }
      if (!group) {
        group = openResearchGroup(items, index);
      }
      if (pendingStepStart) {
        group.parts.push(pendingStepStart);
        pendingStepStart = null;
      }
      group.parts.push({ part, index });
    } else {
      group = null;
      pendingStepStart = null;
      items.push({ kind: "part", part, index });
    }
  });

  // Live, a short line written after the card may still announce the next
  // call: shown under the card first, it moved inside when the call arrived.
  // It goes in at once; if it grows into the answer, it leaves as the card
  // folds — one move instead of two.
  if (isLive) {
    const last = items[items.length - 1];
    if (
      last?.kind === "part" &&
      last.part.type === "text" &&
      isNarration(last.part.text)
    ) {
      const previous = items
        .slice(0, -1)
        .reverse()
        .find((item) => item.kind !== "companyList");
      if (previous?.kind === "researchGroup") {
        items.pop();
        previous.parts.push(narrationPart(last.part.text, last.index));
      }
    }
  }

  return items;
}

/**
 * The last part a live answer has produced, past the step markers and empty
 * text placeholders the stream emits between steps; -1 before the first one.
 */
export function lastMeaningfulPartIndex(parts: readonly AssistantPart[]): number {
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const part = parts[index];
    if (part.type === "step-start" || isTimelinePart(part)) continue;
    if (part.type === "text" && part.text.trim().length === 0) continue;
    return index;
  }
  return -1;
}

export type StreamWaitKind =
  | "thinking"
  | "research"
  | "tactics"
  | "data"
  | "nextStep";

/**
 * How many ways each wait has to say itself, translated under
 * `chat.message.wait.<kind>.<index>`. The line rotates through them during a
 * long wait; index 0 is the plainest, shown first.
 */
export const STREAM_WAIT_VARIANT_COUNT: Readonly<Record<StreamWaitKind, number>> =
  {
    thinking: 8,
    research: 6,
    tactics: 4,
    data: 6,
    nextStep: 6,
  };

// Tools whose card spins on its own while they run: the dot needs no words.
// The tactics tools are the ones that show nothing until they have settled.
const TACTIC_TOOL_TYPES = new Set([
  "tool-listUserTactics",
  "tool-searchUserTactics",
  "tool-activateUserTactic",
]);
// Reads of data the answer is about to be built from.
const DATA_TOOL_TYPES = new Set([
  "tool-getCompanyData",
  "tool-searchHubspotCompanies",
  "tool-searchSeliarisCompanies",
]);

/**
 * What the stream dot says when nothing else on screen moves.
 *
 * A live answer has waits no card shows: the model writing its next tool call,
 * reading what a tool returned, thinking after a sentence (the server does not
 * stream that reasoning). Left alone, the dot and its timer read as a stall.
 * Null when something already moves — the text being written, a research card
 * that is the last thing on screen, a tool card spinning.
 *
 * `isIdle` says the text has not grown for a while: text is the one part whose
 * wait cannot be read off its state, as a sentence being written and a
 * sentence the model has moved past look the same.
 *
 * `trailingItem` is the message's last item past the company list, as
 * {@link lastItemBesideCompanyList} reads it off the render items the message
 * already built — passed in rather than rebuilt here, on every token.
 */
export function streamWaitKind(
  parts: readonly AssistantPart[],
  {
    isIdle,
    trailingItem,
  }: { isIdle: boolean; trailingItem: AssistantRenderItem | undefined }
): StreamWaitKind | null {
  const lastIndex = lastMeaningfulPartIndex(parts);
  if (lastIndex === -1) return "thinking";
  const part = parts[lastIndex];

  // Text first: a line the card took in while it announces the next call
  // leaves no row running, so the dot says the wait once it stops moving.
  if (part.type === "text") {
    return isIdle ? "thinking" : null;
  }

  if (part.type !== "reasoning" && !part.type.startsWith("tool-")) return null;

  // A research card that is the last item shows the wait itself — also when
  // the list it fills came after it, since the list card sits under the block.
  if (trailingItem?.kind === "researchGroup") return null;

  // The card of the work is lifted above the text it interrupts: not the last
  // item, it may sit off-screen, above a long answer.
  if (part.type === "reasoning") return "thinking";
  if (SEARCH_TOOL_TYPES.has(part.type)) return "research";

  const toolPart = part as ToolUIPart;
  if (isToolPartRunning(toolPart)) {
    return TACTIC_TOOL_TYPES.has(part.type) ? "tactics" : null;
  }
  return DATA_TOOL_TYPES.has(part.type)
    ? "data"
    : "nextStep";
}

/**
 * The last item of a message, past the company list: the list card sits under
 * the research block that fills it, so the block is still the wait in progress.
 */
export function lastItemBesideCompanyList(
  items: readonly AssistantRenderItem[]
): AssistantRenderItem | undefined {
  return [...items].reverse().find((item) => item.kind !== "companyList");
}

/** A tool part is still working while its output is only preliminary. */
export function isToolPartRunning(part: ToolUIPart): boolean {
  return (
    part.state === "input-streaming" ||
    part.state === "input-available" ||
    part.state === "approval-requested" ||
    (part.state === "output-available" && part.preliminary === true)
  );
}

/** What a research call is busy with right now, for the folded card. */
export type LiveResearchAction =
  | { kind: "search"; query: string }
  | { kind: "visit"; host: string }
  | { kind: "read"; count: number };

/**
 * What the last research call still running is on: the first query a search
 * runs (the card counts the others),
 * the site a visit reads, the number of pages a read fetches. Null when every
 * call has settled, or when the running one has not written its input yet.
 *
 * A search reads its queries off its snapshot first, then off its input: the
 * input streams in token by token, the snapshot holds what actually runs.
 */
export function liveResearchAction(
  searchParts: readonly ToolUIPart[]
): LiveResearchAction | null {
  // A call whose input is still streaming is named once it is written: the
  // first characters of a query or a URL ("Je lis a") read as noise.
  const part = [...searchParts]
    .reverse()
    .find((call) => isToolPartRunning(call) && call.state !== "input-streaming");
  if (!part) return null;
  const input = part.input as
    | { queries?: unknown; url?: unknown; urls?: unknown }
    | undefined;
  if (part.type === "tool-webSearch") {
    const output = part.output as { queries?: unknown } | undefined;
    const queries = [output?.queries, input?.queries]
      .map((list) =>
        Array.isArray(list)
          ? list.filter(
              (query): query is string =>
                typeof query === "string" && query.trim().length > 0
            )
          : []
      )
      .find((list) => list.length > 0);
    return queries ? { kind: "search", query: queries[0] } : null;
  }
  if (part.type === "tool-scrapeWebsite") {
    const link = typeof input?.url === "string" ? sourceLink(input.url) : null;
    return link ? { kind: "visit", host: link.host } : null;
  }
  if (part.type === "tool-readPages") {
    const count = Array.isArray(input?.urls) ? input.urls.length : 0;
    return count > 0 ? { kind: "read", count } : null;
  }
  return null;
}
